import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Send, CheckCircle2, RotateCcw } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

// Admin side of the support inbox: ticket list + thread with reply and resolve.
export const AdminSupport = ({ onUnreadChange }) => {
  const { getAuthHeader } = useAuth();
  const headers = useCallback(() => ({ headers: { Authorization: getAuthHeader() } }), [getAuthHeader]);
  const [tickets, setTickets] = useState([]);
  const [status, setStatus] = useState('open');
  const [thread, setThread] = useState(null);
  const [text, setText] = useState('');

  const load = useCallback(() => axios.get(`${API}/admin/support/tickets`, { ...headers(), params: { status: status === 'all' ? undefined : status } }).then((r) => { setTickets(r.data); onUnreadChange?.(r.data.reduce((n, t) => n + (t.unread_admin || 0), 0)); }).catch(() => {}), [headers, status, onUnreadChange]);
  const openThread = useCallback((id) => axios.get(`${API}/admin/support/tickets/${id}`, headers()).then((r) => { setThread(r.data); load(); }), [headers, load]);

  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  const reply = async () => {
    if (!text.trim() || !thread) return;
    try { await axios.post(`${API}/admin/support/tickets/${thread.ticket.id}/messages`, { text }, headers()); setText(''); openThread(thread.ticket.id); toast.success('Respuesta enviada'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };
  const setTicketStatus = async (s) => {
    try { await axios.patch(`${API}/admin/support/tickets/${thread.ticket.id}/status`, { status: s }, headers()); openThread(thread.ticket.id); toast.success(s === 'resolved' ? 'Consulta marcada como resuelta' : 'Consulta reabierta'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="grid md:grid-cols-[320px_1fr] gap-4" data-testid="admin-support">
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="p-2 flex gap-1 border-b border-slate-100">
          {['open', 'resolved', 'all'].map((s) => (
            <Button key={s} size="sm" variant={status === s ? 'default' : 'outline'} data-testid={`admin-support-filter-${s}`} onClick={() => setStatus(s)} className={`flex-1 ${status === s ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}>{s === 'open' ? 'Abiertas' : s === 'resolved' ? 'Resueltas' : 'Todas'}</Button>
          ))}
        </div>
        <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100" data-testid="admin-support-list">
          {tickets.length === 0 && <p className="p-6 text-sm text-slate-500 text-center">Sin consultas</p>}
          {tickets.map((t) => (
            <button key={t.id} type="button" data-testid={`admin-ticket-${t.id}`} onClick={() => openThread(t.id)} className={`w-full text-left p-3 hover:bg-slate-50 ${thread?.ticket.id === t.id ? 'bg-emerald-50' : ''}`}>
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-slate-900 truncate flex-1">{t.subject}</p>
                {t.unread_admin > 0 && <span className="min-w-[20px] h-5 px-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold flex items-center justify-center">{t.unread_admin}</span>}
              </div>
              <p className="text-xs text-slate-500 truncate" translate="no">{t.user_name} · {t.user_email}</p>
              <p className="text-[11px] text-slate-400">{fmt(t.updated_at)} · {t.status === 'resolved' ? 'Resuelta' : 'Abierta'}</p>
            </button>
          ))}
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm flex flex-col min-h-[50vh]">
        {!thread ? <p className="p-8 text-sm text-slate-500 text-center m-auto">Selecciona una consulta</p> : (
          <>
            <div className="p-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-slate-900 truncate">{thread.ticket.subject}</p>
                <p className="text-xs text-slate-500 truncate" translate="no">{thread.ticket.user_name} · {thread.ticket.user_email} · {thread.ticket.user_role}</p>
              </div>
              {thread.ticket.status === 'resolved'
                ? <Button size="sm" variant="outline" data-testid="admin-ticket-reopen" onClick={() => setTicketStatus('open')}><RotateCcw size={14} className="mr-1" /> Reabrir</Button>
                : <Button size="sm" variant="outline" data-testid="admin-ticket-resolve" onClick={() => setTicketStatus('resolved')} className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"><CheckCircle2 size={14} className="mr-1" /> Marcar resuelta</Button>}
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50 max-h-[45vh]" data-testid="admin-ticket-messages">
              {thread.messages.map((m) => (
                <div key={m.id} className={`flex ${m.from_admin ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm ${m.from_admin ? 'bg-emerald-600 text-white rounded-br-sm' : 'bg-white border border-slate-200 text-slate-900 rounded-bl-sm'}`}>
                    <p className="text-[10px] font-semibold opacity-80 mb-0.5" translate="no">{m.sender_name}</p>
                    <p className="whitespace-pre-wrap break-words">{m.text}</p>
                    <p className={`text-[10px] mt-1 ${m.from_admin ? 'text-emerald-100' : 'text-slate-400'}`}>{fmt(m.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-3 border-t border-slate-100 flex gap-2">
              <input data-testid="admin-ticket-reply-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); reply(); } }} placeholder="Responder al usuario…" className="flex-1 rounded-full border border-slate-200 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              <Button onClick={reply} disabled={!text.trim()} data-testid="admin-ticket-reply-send" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-4"><Send size={16} /></Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Broadcast an in-app notification + view the admin activity log.
export const AdminTools = () => {
  const { getAuthHeader } = useAuth();
  const headers = useCallback(() => ({ headers: { Authorization: getAuthHeader() } }), [getAuthHeader]);
  const [message, setMessage] = useState('');
  const [audience, setAudience] = useState('all');
  const [log, setLog] = useState([]);
  const loadLog = useCallback(() => axios.get(`${API}/admin/log`, headers()).then((r) => setLog(r.data)).catch(() => {}), [headers]);
  useEffect(() => { loadLog(); }, [loadLog]);

  const send = async () => {
    try {
      const r = await axios.post(`${API}/admin/broadcast`, { message, audience }, headers());
      toast.success(`Comunicado enviado a ${r.data.recipients} usuarios`); setMessage(''); loadLog();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };
  const actionLabel = { delete_listing: 'Borró un anuncio', delete_user: 'Eliminó un usuario', bulk_delete_users: 'Eliminó usuarios en bloque', bulk_delete_listings: 'Eliminó anuncios en bloque', grant_subscription: 'Activó suscripción gratuita', revoke_subscription: 'Revocó suscripción', broadcast: 'Envió un comunicado' };

  return (
    <div className="space-y-4" data-testid="admin-tools">
      <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm space-y-3">
        <h3 className="text-sm font-bold text-slate-900">Comunicado a usuarios</h3>
        <p className="text-xs text-slate-500">Se envía como notificación dentro de la app (campana) a la audiencia elegida.</p>
        <div className="flex gap-2">
          {['all', 'buyers', 'sellers'].map((a) => (
            <Button key={a} size="sm" variant={audience === a ? 'default' : 'outline'} data-testid={`admin-broadcast-audience-${a}`} onClick={() => setAudience(a)} className={audience === a ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}>{a === 'all' ? 'Todos' : a === 'buyers' ? 'Compradores' : 'Vendedores'}</Button>
          ))}
        </div>
        <textarea data-testid="admin-broadcast-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="Ej: Mantenimiento programado el domingo de 3:00 a 4:00" className="w-full rounded-lg border border-slate-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
        <Button onClick={send} disabled={!message.trim()} data-testid="admin-broadcast-send" className="bg-emerald-600 hover:bg-emerald-700 text-white"><Send size={14} className="mr-1" /> Enviar comunicado</Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
        <h3 className="text-sm font-bold text-slate-900 p-4 border-b border-slate-100">Registro de actividad de administradores</h3>
        <div className="max-h-[50vh] overflow-y-auto divide-y divide-slate-100" data-testid="admin-log-list">
          {log.length === 0 && <p className="p-6 text-sm text-slate-500 text-center">Sin actividad registrada</p>}
          {log.map((l, i) => (
            <div key={l.id || i} className="p-3 text-sm flex flex-wrap gap-x-2" data-testid="admin-log-row">
              <span className="text-slate-400 text-xs w-32">{fmt(l.at)}</span>
              <span className="font-medium text-slate-900" translate="no">{l.admin_email || l.admin_id}</span>
              <span className="text-slate-700">{actionLabel[l.action] || l.action}</span>
              <span className="text-slate-500 text-xs" translate="no">{l.target_email || l.kind || l.audience || ''}{l.count != null ? ` · ${l.count} elementos` : ''}{l.months ? ` · ${l.months >= 120 ? 'ilimitada' : `${l.months} meses`}` : ''}{l.recipients != null ? ` · ${l.recipients} destinatarios` : ''}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
